import React from "react";
import * as _ from "lodash";
import * as moment from "moment";

import * as SmartDrawings from "../../smartDrawings";
import { ModelApi } from "../../api/modelApi";
import * as ColorUtils from "./colorUtils";
import { viewerOptions } from "../../viewer";

interface GradientProps {
    viewer: Autodesk.Viewing.GuiViewer3D;
    range: SmartDrawings.RangeColorScheme;
}

interface GradientUiProps {
    viewer: Autodesk.Viewing.GuiViewer3D;
    ranges: SmartDrawings.RangeColorSchemes;
    roomIdToDbId: any;
}

interface GradientUiState {
    range: SmartDrawings.RangeColorScheme;
    measurements: any[];
    selectedNodes: number[];
    pending: boolean;
}

interface MeasurementProps {
    measurement: any;
    range: SmartDrawings.RangeColorScheme;
    selected: boolean;
    onClick: () => void;
}

const SelectOption = props => (<option value={props.id}>{props.description}</option>);

const SelectField = (props) => {
    return <div className="form-group row">
               <label className="col-form-label col-4 text-truncate">{props.label}</label>
               <div className="col-6">
                   <select className="form-control" onChange={(e) => {
                       props.callback(e.target.value);

                   }}>
                       {props.values.map(v => (<SelectOption key={v.id} {...v}/>))}
                   </select>
               </div>
               <button title="Force data refresh" className="btn btn-primary col-1" type="button" disabled={props.disabled} onClick={props.refresh}>
                   <i className="icon ion-md-refresh"></i>
               </button>
           </div>;
}

class ColorGradient extends React.Component<GradientProps> {

    private points: SmartDrawings.RangePoint[];

    constructor(props) {
        super(props);
        this.updatePoints();
    }

    componentDidMount() {
        this.updatePoints();
        this.draw();
    }

    componentDidUpdate() {
        this.updatePoints();
        this.draw();
    }

    private updatePoints() {
        this.points = this.props.range.range.slice(1, this.props.range.range.length - 1);
    }

    draw() {
        const canvas = this.refs.canvas as HTMLCanvasElement;
        const ctx = canvas.getContext("2d");

        const min = _.first(this.points).value;
        const max = _.last(this.points).value;

        const step = (max - min) / 300;

        for (let i = min, j = 0; i < max; i += step, j++) {
            const rgb = ColorUtils.getRgb(i, this.props.range, false);
            ctx.fillStyle = `rgba(${rgb.join(",")}, 1)`;
            ctx.fillRect(j, 0, 1, canvas.clientHeight);
        }
    }

    render() {
        return (
            <div className="ml-2">
                <canvas ref="canvas" style={{ width: "95%", height: "25px" }} width={300} height={25}/>
                <div className="status-meter" style={{ width: "95%" }}>
                    {this.points.map(s => (<div key={s.value}>{s.value}</div>))}
                </div>
            </div>
        );
    }
}

const tempColor = (rgb, outdated) => {
    const style = { backgroundColor: `rgba(${rgb[0]},${rgb[1]},${rgb[2]},1)`, color: "black" };
    if (outdated) {
        style.color = "yellow";
    }
    return style;
};

class MeasurementRow extends React.Component<MeasurementProps> {
    render() {
        const rgb = ColorUtils.getRgb(this.props.measurement.value, this.props.range, this.props.measurement.outdated);
        return <li className="color-item" style={this.props.selected ? { border: "solid white" } : null} onClick={this.props.onClick}>
                   <div className="color-icon" style={tempColor(rgb, this.props.measurement.outdated)}>{this.props.measurement.value}</div>
                   <div className="color-label-short">{this.props.measurement.apartmentId}</div>
                   <div className="color-label">{moment(this.props.measurement.deviceTime).format("D.M.YYYY H:mm")
                   }</div>
               </li>;
    }
}

const Pending = () => {
    return <div>
               <div className="row justify-content-center">
                   <i className="icon ion-md-hourglass wait-spinner"/>
               </div>
           </div>;
}

export default class ColorGradientUi extends React.Component<GradientUiProps, GradientUiState> {
    private timeout;
    private listRefs = {};

    constructor(props) {
        super(props);
        this.state = {
            range: props.ranges.colorRanges[0],
            measurements: [],
            selectedNodes: [],
            pending: true
        }
    }

    componentDidMount(): void {
        this.props.viewer.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.selectionListener);
        this.setNodeColors(this.state.range, false);
    }

    componentWillUnmount(): void {
        if (this.timeout != null) {
            clearTimeout(this.timeout);
        }
        this.props.viewer.removeEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.selectionListener);
    }

    selectionListener = () => {
        const selected = this.props.viewer.getSelection();
        this.setState({ selectedNodes: selected });
        if (selected != null && selected.length > 0) {
            const row = this.listRefs[selected[0]];
            const parent = row.parentNode;
           
            parent.scrollTop = row.offsetTop - parent.offsetTop;
        }
    }

    isSelected(measurement) {
        const dbId = this.props.roomIdToDbId[measurement.apartmentId];
        return _.some(this.state.selectedNodes, sn => sn == dbId);
    }

    onSelectRange = async (rangeId) => {
        const range = _.find(this.props.ranges.colorRanges, s => s.id === rangeId);
        this.setState({ range: range, measurements: [] });
        this.setNodeColors(range, false);
    }

    forceRefresh = () => {
        this.setState({ measurements: [] });
        this.setNodeColors(this.state.range, true);
    }

    render() {
        return (
            <div className="react-content">
                <form className="container pt-3" style={{ width: "auto" }}>
                    <SelectField label={"Displayed data"} values={this.props.ranges.colorRanges} callback={this
                        .onSelectRange} refresh={this.forceRefresh} disabled={this.state.pending}/>
                </form>
                <ColorGradient range={this.state.range} viewer={this.props.viewer}/>
                {this.state.pending && <Pending/>}
                <ul className="plain-list" style={{ height: "200px", overflow: "hidden", overflowY: "scroll" }}>
                    {this.state.measurements.map(m => {
                        const dbId = this.props.roomIdToDbId[m.apartmentId];
                        return <div key={m.sourceId} ref={el => (this.listRefs[dbId] = el)}>
                                   <MeasurementRow
                                measurement={m}
                                range={this.state.range}
                                selected={this.isSelected(m)}
                                onClick={() => this.props.viewer.select(dbId)} />
                               </div>;
                    })}
                </ul>
            </div>
        );
    }

    private calcOutdated(measurements) {
        const ago24 = moment().add(-1, "d");
        return measurements.map(m => {
            m.outdated = moment(m.deviceTime).isBefore(ago24);
            return m;
        });
    }

    private async setNodeColors(range: SmartDrawings.RangeColorScheme, refresh: boolean) {
        if (this.timeout != null) {
            clearTimeout(this.timeout);
        }

        this.setState({ pending: true });
        let data = null;
        try {
            const resp = await ModelApi.getMeasurementData(viewerOptions.guid, range.id, refresh);
            data = resp.data;
        } catch (e) {
            this.timeout = setTimeout(() => this.setNodeColors(range, false), 60000);
            return;
        } 

        if (range.id === this.state.range.id) {
            this.props.viewer.clearThemingColors(null);
            this.props.viewer.showAll();

            const withOutdated = this.calcOutdated(data);
            this.setState({ measurements: withOutdated, pending: false });
            const isolated = [];

            for (let m of withOutdated) {
                const dbId = this.props.roomIdToDbId[m.apartmentId];

                if (dbId != null) {
                    (this.props.viewer as any).setThemingColor(dbId,
                        ColorUtils.getColorVector(m.value, range, m.outdated),
                        null,
                        true);
                    isolated.push(dbId);
                }
            }
            this.props.viewer.isolate(isolated);
        }

        this.timeout = setTimeout(() => this.setNodeColors(range, false), 60000);
    }
}